import React from "react";
import classNames from "classnames";
import { IoClose } from "react-icons/io5";
import isEmpty from "lodash/isEmpty";

// components
import Link from "@/components/Link";
import HeaderNavigation from "../components/HeaderNavigation";
import HirerHeaderSearch from "@/components/HirerHeaderSearch";
import LogoutIcon from "@/components/icons/LogoutIcon";
import HirerHeaderLogo from "../components/HirerHeaderLogo";

// elements
import Button from "@/shared/Button";

// helpers
import { HIRER_NAVIGATION_ROUTES, BASE_NAVIGATION_ROUTES, LEGAL_PAGES, HIRER_ACCOUNT_MOBILE_ROUTES, SUPPORT_PAGES } from "../../constants";
import useUserProfile from "../../../../hooks/useUserProfile";
import { useLockoutModalContext } from "../../../../contexts/LockoutModalContext";
import customDataLayerPush from "../../../../helpers/customDataLayerPush";

// interfaces
import { IHirerHeaderMobile } from "../IHirerHeader";
import { IHeaderNavigationLink } from "../components/IHeaderNavigation";

// assets
import BookDemoIcon from "@/components/icons/BookDemoIcon";
import styles from "@/assets/styles/components/Header/HirerHeader/index.module.scss"


function HirerHeaderMobile({ onClose, onLogout, unreadMessages }: IHirerHeaderMobile) {
  const { isAuthorized, data, permissions } = useUserProfile();
  const { setState } = useLockoutModalContext();

  
  const onLinkClick = (e: React.MouseEvent, route: string) => {
    if (!isAuthorized && !(["/search-results", "/go-search"].includes(route)) && route !== "/") {
      e.preventDefault();
      setState({ open: true });
      customDataLayerPush("lo_fo_modal_popup_shown", null, { lofo_type: "soft lofo" });
    }

    onClose();
  };

  const navigationRoutesToUse = HIRER_NAVIGATION_ROUTES
    .filter(item => (item as IHeaderNavigationLink).route === "/bookings" ? !isAuthorized || permissions.hirer.canAccessBookingsHistory : true)
    .map(item => (item as IHeaderNavigationLink).route === "/my-messages" ? { ...item, badge: unreadMessages } : item);

  const blockClassName = styles["locl-hirer-header-mobile__block"];

  return (
    <div
      className={
        classNames(
          styles["locl-hirer-header-mobile"],
          { [styles["locl-hirer-header-mobile_weri"]]: process.env.NEXT_PUBLIC_APP_WL_CODE === "WERI" }
        )}
      data-testid="locl-hirer-header-mobile"
    >
      <div className={styles["locl-hirer-header-mobile__logo-block"]}>
        <HirerHeaderLogo />
        <span
          className={styles["locl-hirer-header-mobile__logo-block__close-icon"]}
          onClick={onClose}
          data-testid="locl-hirer-header-mobile-close-icon"
        >
          <IoClose/>
        </span>
      </div>
      <div className={blockClassName}>
        <HirerHeaderSearch onAfterSelected={onClose} fullWidth />
        <HeaderNavigation
          navigationLinks={navigationRoutesToUse as IHeaderNavigationLink[]}
          onClick={onLinkClick}
          isMobile
        />
      </div>
      <div className={blockClassName}>
        <div className={styles["locl-hirer-header-mobile__block__nav-account"]}>
          <HeaderNavigation
            navigationLinks={HIRER_ACCOUNT_MOBILE_ROUTES}
            onClick={onLinkClick}
            isMobile
          />
        </div>
      </div>
      <div className={blockClassName}>
        <div className={styles["locl-hirer-header-mobile__block__nav-account"]}>
          <HeaderNavigation
            navigationLinks={SUPPORT_PAGES}
            onClick={onLinkClick}
            isMobile
          />
        </div>
      </div>
      {
        process.env.NEXT_PUBLIC_APP_WL_CODE === "LOCL" && (
          <div className={blockClassName}>
            <ul className={styles["locl-hirer-header-mobile__base-routes"]}>
              {
                BASE_NAVIGATION_ROUTES.filter(item => item.label !== "lo:live").map((item, index) => (
                  <li
                    key={`BASE_NAVIGATION_ROUTES-${index}`}
                    className="locl-hirer-header-mobile__base-routes__item"
                  >
                    <Link href={item.route} className={styles["locl-hirer-header-mobile__base-routes__item-link"]}>
                      {item.label}
                    </Link>
                  </li>
                ))
              }
            </ul>
          </div>
        )
      }
      <div className={blockClassName}>
        {
          (!isAuthorized || isEmpty(data)) && (<>
            <Button
              fullWidth
              to="/login"
              data-testid="locl-hirer-header-unauthorized-login-button"
            >
              Log in
            </Button>
            <Button
              to="/registration"
              fullWidth
              danger
              data-testid="locl-hirer-header-unauthorized-register-button"
            >
              Sign up, it’s free!
            </Button>
          </>)
        }
        <Button
          to="/book-a-demo"
          fullWidth
          className={styles["locl-hirer-header-mobile__book-demo-button"]}
          data-testid="locl-hirer-header-mobile-book-demo-button"
          type="secondary"
          ghost
          icon={<BookDemoIcon />}
        >
          Book a demo
        </Button>
        {
          !!(isAuthorized && !isEmpty(data)) && (
            <Button
              // danger
              ghost
              fullWidth
              icon={<LogoutIcon/>}
              className={styles["locl-hirer-header-mobile__logout-button"]}
              onClick={onLogout}
              data-testid="locl-hirer-header-unauthorized-logout-button"
            >
              Log Out
            </Button>
          )
        }
        <div className={styles["locl-hirer-header-mobile__block-legal"]}>
          {
            LEGAL_PAGES.map((item, index) => (
              <Link
                href={item.route}
                key={`${item.route}-${index}`}
                className={styles["locl-hirer-header-mobile__block-legal__link"]}
                data-testid="locl-hirer-header-mobile-legal-link"
              >
                {item.label}
              </Link>
            ))
          }
        </div>
      </div>
    </div>
  );
}

export default HirerHeaderMobile;
